import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { Card, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { useDataProvider, Loading } from "react-admin";

interface RiskTypeSeries {
  name: string;
  data: number[];
}

interface RiskTypeBarChartData {
  riskTypes: string[];
  series: RiskTypeSeries[];
}

const RmfRiskTypeBarChart: React.FC = () => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const dataProvider = useDataProvider();
  const [chartData, setChartData] = useState<RiskTypeBarChartData | null>(null);

  useEffect(() => {
    let isMounted = true;
    setChartData(null);

    const fetchData = async () => {
      try {
        const response = await dataProvider.getChart(
          "rmf/analytics/risk-type-bar-chart",
          {}
        );
        if (!isMounted) return;
        setChartData(response as RiskTypeBarChartData);
      } catch (error) {
        if (isMounted) {
          setChartData(null);
          console.error("Error fetching RMF risk type bar chart data:", error);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [dataProvider]);

  useEffect(() => {
    if (!chartData) return;
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const option: echarts.EChartsOption = {
      title: {
        text: "Active Risk Level by Type",
        left: "center",
        textStyle: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
          fontWeight: "bold",
          fontSize: "1.5em",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      legend: {
        top: 30,
        textStyle: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: [
        {
          type: "category",
          data: chartData.riskTypes,
          axisLabel: {
            color: theme.palette.mode === "dark" ? "#fff" : "#222",
            rotate: 30,
          },
        },
      ],
      yAxis: [
        {
          type: "value",
          axisLabel: {
            color: theme.palette.mode === "dark" ? "#fff" : "#222",
          },
        },
      ],
      series: chartData.series.map((seriesItem) => ({
        name: seriesItem.name,
        type: "bar",
        stack: "total",
        emphasis: {
          focus: "series",
        },
        data: seriesItem.data,
      })),
    };

    chartInstance.current.setOption(option);
  }, [chartData, theme.palette.mode]);

  return (
    <Card
      sx={{
        height: 300,
        width: "100%",
        padding: "1em",
      }}
    >
      {!chartData ? (
        <Loading />
      ) : !Array.isArray(chartData.series) || chartData.series.length === 0 ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            No Data Found
          </Typography>
        </Box>
      ) : (
        <Box ref={chartRef} sx={{ height: "100%", width: "100%" }} />
      )}
    </Card>
  );
};

export default RmfRiskTypeBarChart;
